import React, { useCallback, useEffect, useMemo, useRef } from "react";
import type { MouseEventHandler } from "react";

import cn from "classnames";

import { TAB_LINE_TEST_ID } from "./vars";
import {
    type BoxComponentWithDefault,
    boxForwardRef,
} from "../../../helpers/common/Box/Box";
import { ButtonCore } from "../../../helpers/common/ButtonCore";
import { useMouseDownAnimation } from "../../../hooks/useMouseDownAnimation";
import { useSpringAnimation } from "../../../hooks/useSpringAnimation";
import { getChildrenRecord } from "../../../utils/react";
import {
    COUNTER_INLINE_TEST_ID,
    CounterInline,
} from "../../CounterInline/CounterInline";
import { UniBoxAdditional } from "../../UniBox";

import type { TabProps } from "./types";

import classes from "./Tab.styl";

const SPRING_OPTIONS = {
    tension: 220,
    friction: 15,
    timeInMs: 500,
    from: 0,
    to: 1,
    max: 1.1,
};
const ORDINARY_MOUSEDOWN_TIMEOUT = 300;
const BASE_MOUSEDOWN_TIMEOUT = 200;
const TAB_DISPLAY_NAME = "Tab";

/**
 * Базовый компонент, при помощи них можно перемещаться между вкладками в контенте.
 *
 * В качестве `as` ожидаем, что будет передан 'button', 'a' или компонент, реализующий функцию навигации (например, `RoutingLink` на большом вебе).
 * Более подробную информацию можно найти [тут](/docs/документация-разработчикам-дс-инструкция-по-разработке-полиморфных-компонентов--docs).
 *
 * <a href="https://www.figma.com/design/LYZAXE33pUg2cSL5mmUHYq/%F0%9F%8D%91-Buttons-%26-Tabs-%26-Chips?m=auto&node-id=1-97209&t=3ryRFv5yEABAv4kK-1" target="_blank">Спека</a>
 * @constructor
 * @author Vladislav Yartsev
 */
export const Tab = boxForwardRef<BoxComponentWithDefault<"button", TabProps>>(
    (
        {
            as = "button",
            customColorsStyles,
            onMouseUp,
            onMouseDown,
            onClick,
            onFocus,
            view = "primary",
            selected = false,
            "data-tsid": tsid,
            children,
            tabIndex = 0,
            ...props
        },
        ref,
    ) => {
        const {
            clicked: clickedOnOrdinaryTab,
            mousedownHandler: onOrdinaryMousedown,
        } = useMouseDownAnimation(ORDINARY_MOUSEDOWN_TIMEOUT);
        const { clicked: clickedOnBaseTab, mousedownHandler: onBaseMousedown } =
            useMouseDownAnimation(BASE_MOUSEDOWN_TIMEOUT);
        const spring = useSpringAnimation(SPRING_OPTIONS);
        const selectionRef = useRef({
            onInit: selected,
            prevSelected: selected,
        });

        useEffect(() => {
            const justSelected =
                !selectionRef.current.prevSelected &&
                selected &&
                !selectionRef.current.onInit;

            if (justSelected) {
                spring.start();
            }
            selectionRef.current.prevSelected = selected;
        }, [selected, spring]);

        const tabContainerClasses = cn(
            classes["tab-wrapper"],
            classes[`__view-${view}` as keyof typeof classes],
            {
                [classes["__selected"]]: selected,
                [classes["__clicked"]]:
                    clickedOnBaseTab || clickedOnOrdinaryTab,
            },
            customColorsStyles,
        );

        const { Counter, Label, LeftIcon, RightIcon } = useMemo(() => {
            const counterParams: Record<string, object> = {
                props: { size: view === "base" ? 8 : 16 },
            };

            if (view === "primary") {
                counterParams.props = {
                    ...counterParams.props,
                    view: "custom",
                    customColorsStyles:
                        classes["tab-primary-counter-custom-selector"],
                };
            }

            return getChildrenRecord(children, "Tab", {
                Counter: counterParams,
                Label: {
                    required: true,
                },
                LeftIcon: {},
                RightIcon: {},
                Unknown: {
                    skipElement: true,
                },
            });
        }, [children, view]);

        const mouseDownHandler: MouseEventHandler<HTMLButtonElement> = (
            evt,
        ) => {
            onMouseDown?.(evt);

            if (view === "base" && selected) {
                onBaseMousedown();
            } else {
                onOrdinaryMousedown();
            }
        };

        const onClickHandler: MouseEventHandler<HTMLButtonElement> =
            useCallback(
                (evt) => {
                    if (!selected && view === "base") {
                        spring.start();
                    }
                    onClick?.(evt);
                },
                [spring, onClick, selected, view],
            );

        const shouldShowLineImmediately =
            selectionRef.current.onInit && selected && spring.value === 0;

        return (
            <ButtonCore
                as={as}
                {...props}
                type="button"
                wrapperClassName={tabContainerClasses}
                overlaysClassName={classes["overlays-container"]}
                role="tab"
                aria-selected={selected}
                tabIndex={tabIndex}
                data-tsid={tsid}
                data-uikit={TAB_DISPLAY_NAME}
                onMouseDown={mouseDownHandler}
                onMouseUp={onMouseUp}
                onClick={onClickHandler}
                onFocus={onFocus}
                ref={ref}
            >
                <div className={classes["inner-wrapper"]}>
                    {Counter && (
                        <UniBoxAdditional>
                            <UniBoxAdditional.SlotTopRight
                                customVarsStyles={classes["counter-wrapper"]}
                            >
                                {Counter}
                            </UniBoxAdditional.SlotTopRight>
                        </UniBoxAdditional>
                    )}
                    <span className={classes["tab"]}>
                        <span className={classes["content"]}>
                            {LeftIcon && (
                                <span className={classes["left-icon-wrapper"]}>
                                    {LeftIcon}
                                </span>
                            )}
                            <span className={classes["label"]}>{Label}</span>
                            {view === "base" && Counter && (
                                <CounterInline
                                    quantity={Counter.props.quantity}
                                    data-tsid={COUNTER_INLINE_TEST_ID}
                                />
                            )}
                            {RightIcon && (
                                <span className={classes["right-icon-wrapper"]}>
                                    {RightIcon}
                                </span>
                            )}
                        </span>
                    </span>
                </div>
                {view === "base" && (
                    <div
                        className={classes["line"]}
                        style={{
                            transform: `scaleX(${
                                shouldShowLineImmediately ? 1 : spring.value
                            })`,
                        }}
                        data-tsid={TAB_LINE_TEST_ID}
                    />
                )}
            </ButtonCore>
        );
    },
);

Tab.displayName = TAB_DISPLAY_NAME;

export default Tab;
